/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';

import {
  makePaymentWithoutOfficialBill,
  setShowDiscountError,
} from 'redux/payments/payments.actions';
import {
  LeftSideBox,
  PackageFeatures,
  SelectDuration,
  SelectPackage,
  InputPaymentInfoModal,
  SelectBankModal,
} from '.';
import {
  dispalySelectBankModalStart,
  fetchPackagesStart,
  setDiscountCode,
  setIsOfficialBillNeeded,
  setSelectedDuration,
  setSelectedPackage,
  setSelectedPackageType,
  setSelectedUpgradeMethod,
  setUseCredit,
  validateDiscountCodeStart,
} from 'redux/payments/payments.actions';
import {
  MobileVerificationModal,
  ApiLoader,
  HeaderSectionIcon,
  Radio,
  Modal,
  ModalBody,
  ModalFooter,
} from 'components';
import { validateSmsAndRedirectToPaymentStart } from 'redux/user/user.actions';
import useTranslation from 'hooks/useTranslation';
import styles from './PackageUpgrade.module.scss';
import { capitalizeFirstLetter, replaceKeyWithValue } from 'utils/text';
import DashboardSectionHeader from 'layouts/Dashboard/DashboardSectionHeader';
import DashboardMainSectionWrapper from 'layouts/Dashboard/DashboardMainSectionWrapper';
import { ReactComponent as DownloadIcon } from 'assets/icons/download-icon.svg';
import PackageUpgradeStep1 from './PackageUpgradeStep1';
import {
  ArrowDownSquareIcon,
  CheckCircleSolidIconComponent,
  CheckoutListIconComponent,
  ChevronDownIcon,
  InfoCircleIconComponent,
  InfoIconComponent,
  NewDiscountBadgeIconComponent,
  NewDownloadIconComponent,
  PenIconComponent,
  TickSquareIconComponent,
  UserInformationIconComponent,
} from 'assets/icons';
import { calculatePrices, getDurationKey, getIcon } from 'utils/packages';
import { redirectToPaymentPort, splitPriceByThree } from 'utils';
import { useForm, useModal } from 'hooks';
import { validateLeftSideBox } from './LeftSideBox/validateLeftSideBox';
import { getArrayOfInfoBasedOnUserType } from 'redux/payments/payments.adapter';
import EditUserInfo from './EditUserInfo';
import GetInfoFromUser from './GetInfoFromUser';

const isPackageUpgrade = true;

const mapState = (state) => ({
  packages: state.paymentsData.packages,
  selectedPackage: state.paymentsData.selectedPackage,
  selectedDuration: state.paymentsData.selectedDuration,
  isOfficialBillNeeded: state.paymentsData.isOfficialBillNeeded,
  invoiceId: state.paymentsData.invoiceId,
  isAutoUpgradeVisible: state.paymentsData.isAutoUpgradeVisible,
  isSelectBankVisible: state.paymentsData.isSelectBankVisible,
  selectedPackageType: state.paymentsData.selectedPackageType,
  discountCode: state.paymentsData.discountCode,
  discountInfo: state.paymentsData.discountInfo,
  useCredit: state.paymentsData.useCredit,
  userCredit: state.userData.profile.userCredit,
  payablePrice: state.paymentsData.payablePrice,
  selectedUpgradeMethod: state.paymentsData.selectedUpgradeMethod,
  selectedConversationCount: state.paymentsData.selectedConversationCount,
  paymentInfo: state.paymentsData.paymentInfo,
});

const getDiscountErrorObj = ({ paymentsData }) => paymentsData.discountError;
const getProfile = ({ userData }) => userData.profile;

const PackageUpgrade = (props) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState('STEP_1');
  const [showUserInfoDetails, setShowUserInfoDetails] = useState(false);
  const profile = useSelector(getProfile);
  const {
    packages,
    selectedPackage,
    selectedDuration,
    isOfficialBillNeeded,
    invoiceId,
    selectedPackageType,
    discountCode,
    discountInfo,
    useCredit,
    userCredit,
    selectedUpgradeMethod,
    payablePrice,
    selectedConversationCount,
    paymentInfo,
    isAutoUpgradeVisible,
  } = useSelector(mapState);
  const discountErrorObj = useSelector(getDiscountErrorObj);
  const [visible, toggle] = useModal();
  const [checkBeforePurchaseStatus, setCheckBeforePurchaseStatus] =
    useState(false);

  const infoData = paymentInfo.data;
  const anyInfoData = infoData && Object.keys(infoData).length !== 0;
  const formData = getArrayOfInfoBasedOnUserType({
    ...infoData,
    mobileNumber: profile.mobile,
    mobile: profile.mobile,
  });

  const { getTranslation, getAppLocale } = useTranslation([
    'PACKAGE_UPGRADING',
    'BILLS_LIST',
  ]);
  const locale = getAppLocale();
  const { t } = getTranslation();

  const handleValidateDiscountCode = (values) => {
    dispatch(
      validateDiscountCodeStart({
        code: values.discountCode,
        selectedPackageType,
      })
    );
  };

  const handleSetUseCredit = (event) => {
    dispatch(setUseCredit(event.target.checked));
  };

  const {
    values,
    errors,
    handleChange,
    handleSubmit: handleFormSubmit,
  } = useForm(validateLeftSideBox, handleValidateDiscountCode, {
    discountCode: '',
  });

  const handleDurationChange = (event) => {
    dispatch(setSelectedDuration(event.target.value));
  };

  const handleSetIsOfficialBillNeeded = (event) => {
    dispatch(setIsOfficialBillNeeded(event.target.checked));
  };

  useEffect(() => {
    handleSetIsOfficialBillNeeded({ target: { checked: false } });
    if (!packages.loading && !packages.data) {
      dispatch(fetchPackagesStart(getAppLocale()));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const handleSubmit = (values) => {
    dispatch(
      validateSmsAndRedirectToPaymentStart({
        ...values,
        invoiceId,
        isPackageUpgrade: true,
      })
    );
  };

  const getPrice = () => {
    if (!packages?.data) return 0;
    return selectedDuration === 'yearly'
      ? Number(packages.data[selectedPackage].yearlyPrice[locale])
      : selectedDuration === 'quarterly'
      ? Number(packages.data[selectedPackage].quarterlyPrice[locale])
      : selectedDuration === 'triennial'
      ? Number(packages.data[selectedPackage].triennialPrice[locale])
      : selectedDuration === 'monthly' &&
        Number(packages.data[selectedPackage].monthlyPrice[locale]);
  };

  const { totalPrice, postPrice, tax, discountPercent, originaTotalPrice } =
    calculatePrices({
      price: getPrice(),
      isOfficialBillNeeded,
      useCredit,
      userCredit,
      discountInfo,
      selectedPackageType,
      selectedDuration,
      locale: getAppLocale(),
    });

  const selectUpgradeMethodHandler = (event) => {
    dispatch(setSelectedUpgradeMethod(event.target.value));
  };

  const renderHeader = (
    <DashboardSectionHeader>
      <header
        className={clsx(styles.packageUpgradeHeader, 'flex flex-colgap-8')}
      >
        <HeaderSectionIcon />

        <div>
          <h1 className={clsx(styles.h1, 'main_section_header_title')}>
            {t('HEADING_TITLE')}
          </h1>
          <p className={clsx(styles.description, 'main_section_header_desc')}>
            {t('HEADING_SUBTITLE')}
          </p>
        </div>
      </header>
    </DashboardSectionHeader>
  );

  const handlePayment = () => {
    if (checkBeforePurchaseStatus) {
      toggle();
      return;
    }

    const invoiceDataForFaLocale = isPackageUpgrade
      ? {
          isPackageUpgrade,
          isOfficialBillNeeded,
          selectedPackage,
          selectedDuration,
          discountCode,
          useCredit,
          payablePrice,
          selectedPackageType,
        }
      : {
          isPackageUpgrade,
          isOfficialBillNeeded,
          selectedConversationCount,
          payablePrice,
        };

    dispatch(makePaymentWithoutOfficialBill({ ...invoiceDataForFaLocale }));
    // if (locale === 'en') {
    //   redirectToPaymentPortEng(invoiceId);
    //   return;
    // }

    // redirectToPaymentPort(invoiceId);
  };

  useEffect(() => {
    if (isAutoUpgradeVisible) {
      redirectToPaymentPort(invoiceId);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoUpgradeVisible]);

  const dispaySelectBankModalHandler = (event) => {
    dispatch(dispalySelectBankModalStart({ handleToggleModal: () => {} }));
  };

  const renderSelectDeliveryType = locale === 'fa' && (
    <div className={styles.select_delivery_type}>
      <div className='flex flex-colgap-8'>
        <CheckCircleSolidIconComponent />
        <span>
          {t('DOWNLOAD_FACTOR_LATER')}
          <Link to='/payment/bills'>لیست صورتحساب‌ها</Link>
        </span>
      </div>

      {/* <div className='flex flex-ai-c'>
        <input
          type='checkbox'
          id='isOfficialBillNeeded'
          onChange={handleSetIsOfficialBillNeeded}
          checked={isOfficialBillNeeded}
        />
        <label htmlFor='isOfficialBillNeeded' aria-describedby='label'>
          {t('SEND_OFFICIAL_INVOICE')}
        </label>
      </div> */}
      {/* <div className={styles.selectDeliveryType__subtext}>
        <DownloadIcon className={styles.selectDeliveryType__subtext__icon} />
        <div>
          <span>دانلود فاکتور رسمی: تمدید یا ارتقای پکیج </span>
          {' > '}
          <Link to='/payment/bills'>لیست صورتحساب‌ها</Link>
        </div>
      </div> */}
    </div>
  );

  const renderAutomaticWithdrawOption = locale === 'fa' && (
    <div>
      <Radio
        label={t('SERVICE_AUTO_RENEWAL')}
        value='auto'
        checkedValue={selectedUpgradeMethod}
        onChecked={selectUpgradeMethodHandler}
      />
    </div>
  );

  const handleCancleDiscountCode = () => {
    dispatch(setDiscountCode({ discountCode: '', discountInfo: null }));
  };

  // const handleMakePaymentWithoutOfficialBill = () => {
  //   const invoiceDataForFaLocale = isPackageUpgrade
  //     ? {
  //         isPackageUpgrade,
  //         isOfficialBillNeeded,
  //         selectedPackage,
  //         selectedDuration,
  //         discountCode,
  //         useCredit,
  //         payablePrice,
  //         selectedPackageType,
  //       }
  //     : {
  //         isPackageUpgrade,
  //         isOfficialBillNeeded,
  //         selectedConversationCount,
  //         payablePrice,
  //       };

  //   dispatch(makePaymentWithoutOfficialBill(invoiceDataForFaLocale));
  // };

  useEffect(() => {
    if (discountErrorObj?.show) {
      setTimeout(() => {
        dispatch(
          setShowDiscountError({
            discountError: '',
            show: false,
          })
        );
      }, 3000);
    }
  }, [discountErrorObj?.show]);

  return (
    <div className=''>
      {renderHeader}
      <DashboardMainSectionWrapper>
        {packages.data && !packages.loading && (
          <>
            <section className={styles.packageUpgrade}>
              {step === 'STEP_1' ? (
                <PackageUpgradeStep1 setStep={setStep} />
              ) : (
                <>
                  <div className={styles.second_step_wrapper}>
                    <div className={styles.second_step_header}>
                      <h2 className={styles.second_step_header_title}>
                        {t('DETAILS_MODAL').TITLE}
                      </h2>

                      <div className={styles.download_factor}>
                        <a
                          href='/dashboard/payment/bills'
                          className={styles.download_factor_link}
                          target='_blank'
                        >
                          <CheckoutListIconComponent />
                          لیست پرداخت ها
                        </a>
                      </div>
                    </div>

                    <div className={styles.completing_infos_wrapper}>
                      <div className={styles.info_items_wrapper}>
                        <div className={clsx(styles.info_item)}>
                          <span className={styles.info_item_key}>
                            {t('SELECTED_PACKAGE')}
                          </span>

                          <div className='flex flex-ai-c flex-colgap-12'>
                            <img src={getIcon(selectedPackage)} alt='' />

                            <span className={styles.info_item_value}>
                              {
                                packages.data[selectedPackage].name[
                                  getAppLocale()
                                ]
                              }
                            </span>
                          </div>
                        </div>

                        <div className={clsx(styles.info_item)}>
                          <span className={styles.info_item_key}>
                            {t('DURATION')}
                          </span>

                          <div className='flex flex-ai-c flex-colgap-12'>
                            <span className={styles.info_item_value}>
                              {selectedDuration === 'yearly'
                                ? t('YEARLY')
                                : selectedDuration === 'triennial'
                                ? t('TRIENNIAL')
                                : selectedDuration === 'quarterly'
                                ? t('QUARTERLY')
                                : t('MONTHLY')}
                            </span>
                          </div>
                        </div>

                        <div className={styles.select_delivery_type_mobile}>
                          {renderSelectDeliveryType}
                        </div>

                        {/* <div className={clsx(styles.info_item)}>
                          <span className={styles.info_item_key}>
                            {t('POSTING')}
                          </span>

                          <div className='flex flex-ai-c flex-colgap-12'>
                            <span className={styles.info_item_value}>
                              {splitPriceByThree(postPrice)}
                            </span>
                          </div>
                        </div> */}

                        {/* <div className={clsx(styles.info_item)}>
                          <span className={styles.info_item_key}>
                            {t('TAX')}
                          </span>

                          <div className='flex flex-ai-c flex-colgap-12'>
                            <span className={styles.info_item_value}>
                              {splitPriceByThree(tax)} {t('UNIT')}
                            </span>
                          </div>
                        </div> */}

                        <div className={styles.revive_options_box}>
                          <div className={styles.revive_options_list}>
                            <div>
                              <Radio
                                label={t('SERVICE_MANUAL_RENEWAL')}
                                value='manual'
                                checkedValue={selectedUpgradeMethod}
                                onChecked={selectUpgradeMethodHandler}
                              />
                            </div>
                            {renderAutomaticWithdrawOption}
                          </div>

                          <div
                            className={styles.desc}
                            data-show={selectedUpgradeMethod === 'auto'}
                          >
                            <InfoIconComponent />
                            <p>{t('SERVICE_AUTO_RENEWAL_DESCRIPTION')}</p>
                          </div>
                        </div>

                        <div className={styles.discount_credit_box}>
                          <div className={styles.discount_box}>
                            <div className='flex flex-ai-c flex-colgap-12'>
                              <NewDiscountBadgeIconComponent />

                              {t('USE_DISCOUNT')}
                              {' : '}
                            </div>

                            <div className={styles.inputWrapper}>
                              {(!discountInfo ||
                                (discountInfo && !discountInfo.offPrice)) && (
                                <>
                                  <input
                                    name='discountCode'
                                    value={values.discountCode}
                                    onChange={handleChange}
                                    className={clsx(
                                      errors.discountCode && 'has-error'
                                    )}
                                    disabled={useCredit ? true : false}
                                    type='text'
                                    placeholder={t('ENTER_DISCOUNT_CODE')}
                                  />
                                  <button
                                    className={clsx(styles.btn, {
                                      'btn-primary-disabled': useCredit,
                                    })}
                                    onClick={handleFormSubmit}
                                    disabled={useCredit ? true : false}
                                  >
                                    {t('CHECK')}
                                  </button>
                                </>
                              )}
                              {errors.discountCode && (
                                <div className={styles.error}>
                                  {t('ENTER_DISCOUNT_CODE')}
                                </div>
                              )}
                              {discountInfo && discountInfo.offPrice && (
                                <>
                                  <div
                                    className={styles.successfullDiscountCode}
                                  >
                                    <div className='flex flex-ai-c flex-colgap-4'>
                                      <TickSquareIconComponent />
                                      <span>
                                        {/* {t('DISCOUNT_CODE_APPLIED_PURE')} */}
                                        {replaceKeyWithValue(
                                          t('DISCOUNT_CODE_APPLIED'),
                                          'code',
                                          discountCode
                                        )}
                                      </span>
                                    </div>
                                    <span
                                      onClick={handleCancleDiscountCode}
                                      className={styles.cancel_btn}
                                    >
                                      {t('CANCEL_CODE')}
                                    </span>
                                  </div>
                                </>
                              )}

                              <div
                                className={styles.beautiful_discount_error_box}
                                data-show={discountErrorObj.show}
                              >
                                <InfoCircleIconComponent />
                                {discountErrorObj.errorMsg}
                              </div>
                            </div>
                          </div>

                          <div className={styles.useCreditWrapper}>
                            <div className={styles.formControl}>
                              <input
                                type='checkbox'
                                id='useCredit'
                                onChange={handleSetUseCredit}
                                disabled={
                                  userCredit === 0
                                    ? true
                                    : !discountCode
                                    ? false
                                    : true
                                }
                                checked={useCredit}
                              />
                              <label
                                htmlFor='useCredit'
                                aria-describedby='label'
                              >
                                {t('USE_CREDIT')}
                              </label>
                            </div>
                            <span className={styles.credit_amount}>
                              {splitPriceByThree(userCredit)}{' '}
                              {getAppLocale() === 'fa'
                                ? t('TOOMAN')
                                : t('UNIT')}
                            </span>
                          </div>
                        </div>

                        <div className={styles.price_details_wrapper}>
                          <div className={styles.price_detail_item}>
                            <span className={styles.price_detail_item_key}>
                              {t('PACKAGE_PRICE')}
                            </span>

                            <span className={styles.price_detail_item_value}>
                              {packages.data[selectedPackage][
                                getDurationKey(selectedDuration)
                              ][locale]?.toLocaleString()}{' '}
                              {t('UNIT')}
                            </span>
                          </div>

                          <div className={styles.price_detail_item}>
                            <span className={styles.price_detail_item_key}>
                              {t('DISCOUNT_CODE_PERCENT')}
                            </span>

                            <span
                              className={clsx(
                                styles.price_detail_item_value,
                                styles.price_detail_item_value_discount
                              )}
                            >
                              {discountPercent
                                ? Math.round(
                                    (originaTotalPrice || 0) *
                                      (Number(discountPercent) / 100)
                                  )
                                : 0}{' '}
                              {getAppLocale() === 'fa'
                                ? t('TOOMAN')
                                : t('UNIT')}
                            </span>
                          </div>

                          <div className={styles.price_detail_item}>
                            <span className={styles.price_detail_item_key}>
                              {t('TAX')}
                            </span>

                            <span className={styles.price_detail_item_value}>
                              {(tax || 0)?.toLocaleString()} {t('UNIT')}
                            </span>
                          </div>

                          {/* <div className={styles.price_detail_item}>
                            <span className={styles.price_detail_item_key}>
                              {t('POSTING')}
                            </span>

                            <span className={styles.price_detail_item_value}>
                              {(postPrice || 0)?.toLocaleString()} {t('UNIT')}
                            </span>
                          </div> */}
                        </div>

                        <div className={styles.final_prices_wrapper}>
                          {!!discountPercent && discountPercent !== '0' && (
                            <div className={styles.original_price}>
                              <div className={styles.original_price_key}>
                                {t('PRICE_BEFORE_DISCOUNT')}
                              </div>

                              <div className={styles.original_price_values}>
                                <div className={styles.original_price_value}>
                                  {(originaTotalPrice || 0)?.toLocaleString()}
                                </div>

                                <div className={styles.dis_box}>
                                  {discountPercent}%
                                </div>
                              </div>
                            </div>
                          )}

                          <div className={styles.final_price}>
                            <div className={styles.final_price_key}>
                              {t('PAYABLE')}
                            </div>

                            <div className={styles.final_price_values}>
                              <div className={styles.final_price_value}>
                                {(totalPrice || 0)?.toLocaleString()}
                              </div>

                              <span className={styles.final_price_unit}>
                                {t('UNIT')}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className={styles.step_two_actions_wrapper}>
                          {selectedUpgradeMethod === 'manual' ? (
                            <a
                              href='#'
                              className={styles.main_action_btn}
                              id='saveClose'
                              onClick={handlePayment}
                            >
                              {t('REDIRECT_TO_PAYMENT_GATEWAY')}
                            </a>
                          ) : (
                            <a
                              href='#'
                              className={styles.main_action_btn}
                              id='saveClose'
                              onClick={dispaySelectBankModalHandler}
                            >
                              {t('CONTINUE_PURCHASE')}
                            </a>
                          )}

                          <div
                            className={styles.go_back_btn}
                            onClick={() => setStep('STEP_1')}
                          >
                            {t('GO_TO_PREV_STEP')}
                          </div>
                        </div>
                      </div>
                      <div className={styles.checkout_bill_wrapper}>
                        <div className={styles.user_info_details_wrapper}>
                          <div
                            className={'flex flex-ai-c flex-jc-sb'}
                            onClick={() => {
                              setShowUserInfoDetails((prev) => !prev);
                            }}
                          >
                            <div className='flex flex-ai-c flex-colgap-8'>
                              <UserInformationIconComponent />
                              <span className={styles.account_info}>
                                {t('ACCOUNT_INFO')}
                              </span>
                            </div>

                            <div className={styles.edit_user_info_wrapper}>
                              <EditUserInfo isPackageUpgrade={true} />
                            </div>

                            <div
                              className={styles.arrow_open}
                              data-open={showUserInfoDetails}
                            >
                              <ChevronDownIcon />
                            </div>
                          </div>

                          <div
                            className={styles.user_infos_wrapper}
                            data-show={showUserInfoDetails}
                          >
                            <div
                              className={styles.edit_user_info_wrapper_inside}
                            >
                              <EditUserInfo isPackageUpgrade={true} />
                            </div>
                            {formData?.map(
                              (item, index) =>
                                item && (
                                  <div
                                    key={index}
                                    className={styles.user_info_item}
                                  >
                                    <span className={styles.user_info_item_key}>
                                      {item.label}
                                    </span>
                                    <span
                                      className={styles.user_info_item_value}
                                    >
                                      {item.value}
                                    </span>
                                  </div>
                                )
                            )}
                          </div>
                        </div>

                        <div className={styles.select_delivery_type_desktop}>
                          {renderSelectDeliveryType}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className={`${styles.row} ${styles.container}`}> */}
                  {/* <div className={clsx(styles.column, styles.right)}>
                      <PackageFeatures
                        name={
                          packages.data[selectedPackage].name[getAppLocale()]
                        }
                        features={packages.data[selectedPackage].features}
                      />

                      <SelectDuration
                        title={t('MONTHLY')}
                        price={
                          packages.data[selectedPackage].monthlyPrice[locale]
                        }
                        value='monthly'
                        unit={` ${t('UNIT')} / ${t('MONTHLY')}`}
                        onRadioChange={handleDurationChange}
                        selectedDuration={selectedDuration}
                        disable={selectedPackage === 'professional'}
                      />

                      <SelectDuration
                        title={t('QUARTERLY')}
                        price={
                          packages.data[selectedPackage].quarterlyPrice[locale]
                        }
                        value='quarterly'
                        unit={` ${t('UNIT')} / ${t('QUARTERLY')}`}
                        onRadioChange={handleDurationChange}
                        selectedDuration={selectedDuration}
                      />

                      <SelectDuration
                        title={t('YEARLY')}
                        value='yearly'
                        price={
                          packages.data[selectedPackage].yearlyPrice[locale]
                        }
                        // description={getDiscountDescription('yearly')}
                        unit={` ${t('UNIT')} / ${t('YEARLY')}`}
                        onRadioChange={handleDurationChange}
                        selectedDuration={selectedDuration}
                      />

                      {selectedPackage !== 'professional' && (
                        <SelectDuration
                          title={t('TRIENNIAL')}
                          price={
                            packages.data[selectedPackage].triennialPrice[
                              locale
                            ]
                          }
                          value='triennial'
                          // description={getDiscountDescription('triennial')}
                          unit={` ${t('UNIT')} / ${t('TRIENNIAL')}`}
                          onRadioChange={handleDurationChange}
                          selectedDuration={selectedDuration}
                        />
                      )}
                      {renderSelectDeliveryType}
                    </div> */}

                  {/* <div className={clsx(styles.column, styles.leftSidebox)}>
                      <LeftSideBox
                        isOfficialBillNeeded={isOfficialBillNeeded}
                        price={getPrice()}
                      />
                    </div> */}
                  {/* </div> */}
                </>
              )}
            </section>
            <GetInfoFromUser
              isPackageUpgrade={isPackageUpgrade}
              toggle={toggle}
              visible={visible}
              checkBeforePurchaseStatus={checkBeforePurchaseStatus}
              setCheckBeforePurchaseStatus={setCheckBeforePurchaseStatus}
            />
            <SelectBankModal />
          </>
        )}
        {!packages.data && packages.loading && <ApiLoader />}
      </DashboardMainSectionWrapper>
    </div>
  );
};

export default PackageUpgrade;
